import { skipToken } from '@reduxjs/toolkit/query/react';

import { type ConnectionHealth, useGetConnectionHealthQuery } from '../services/pmsConnectionApi';
import type { PmsAccountRef } from '../services/pmsAccount';

const UNKNOWN: ConnectionHealth = { kind: 'unknown' };

/**
 * Stored-credential health for the account's PMS connection. Fails OPEN:
 * loading, transport errors, 404 (backend not deployed) and 403 (path not
 * yet a Keycloak resource) all read as `unknown`, so callers fall back to
 * the sync-log signal rather than painting a false "Inactive".
 */
export function useConnectionHealth(
  account: PmsAccountRef | null,
  options: { skip?: boolean } = {}
): ConnectionHealth {
  return useConnectionHealthState(account, options).health;
}

/**
 * The same health, plus whether it is the answer yet (DV-3500). `isSettled`
 * is false only while the probe is in flight for this account: a refused or
 * failed probe is settled (`unknown` IS its answer), and so is a skipped one.
 * The internal account page holds its pill until then instead of painting
 * Active and flipping.
 */
export function useConnectionHealthState(
  account: PmsAccountRef | null,
  options: { skip?: boolean } = {}
): { health: ConnectionHealth; isSettled: boolean } {
  const skipped = account === null || options.skip === true;
  // `currentData`, never `data`: when the account changes, RTK keeps the
  // PREVIOUS account's answer in `data` until the new one settles (DV-3500).
  const { currentData, isError } = useGetConnectionHealthQuery(skipped ? skipToken : account);
  return {
    health: currentData ?? UNKNOWN,
    isSettled: skipped || currentData !== undefined || isError,
  };
}
